import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import "./i18n";
import { captureAttribution } from "./lib/attribution";

// Sentry browser error monitoring
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Filter out well-known third-party browser noise that doesn't represent
    // real user-facing bugs. Each entry is matched against the error message.
    // Strings are partial matches; regexes are exact match logic.
    ignoreErrors: [
      // Microsoft Outlook SafeLinks pre-fetch (CefSharp-based crawler) injects
      // a JS bridge that fails silently. Pattern:
      //   "Object Not Found Matching Id:3, MethodName:update, ParamCount:4"
      // See: https://github.com/getsentry/sentry-javascript/issues/3440
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      // ResizeObserver loop limit / loop completed with undelivered
      // notifications: benign browser warning, not a real error. Chromium
      // surfaces it as an uncaught error in older versions.
      /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
      // Facebook/Instagram in-app browser bridge noise.
      "fb_xd_fragment",
      // MetaMask / other Web3 wallet browser extensions auto-inject into every
      // page and throw connect errors on sites that don't use Web3. We are an
      // ICS calendar app and never touch wallets. Sample stacktrace frame:
      //   chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn/scripts/inpage.js
      // (nkbihfbeogaeaoehlefnkodbefgpgknn is the MetaMask extension ID)
      /Failed to connect to MetaMask/,
      /MetaMask extension not found/,
      // Browser-translator DOM mutation crashes. When Chrome/Edge/Safari
      // auto-translate the page, they replace text nodes with translated
      // equivalents. React's commit phase then tries to update/unmount the
      // original node and the parent no longer owns it, throwing NotFoundError
      // or HierarchyRequestError. Caught by ErrorBoundary so the page recovers;
      // not actionable in our code. See WC-CALENDAR-5 (es-ES visitor on mobile
      // Chrome). Mirrors a long-standing React/Google-Translate interaction:
      //   https://github.com/facebook/react/issues/11538
      /Failed to execute 'removeChild' on 'Node'/,
      /Failed to execute 'insertBefore' on 'Node'/,
      /The node to be removed is not a child of this node/,
      /The node before which the new node is to be inserted is not a child of this node/,
      // Cashback/coupon shopping extensions (Honey, Klarna and similar) inject
      // scripts inline into the page context, so denyUrls can't catch them.
      // They throw on their own internal response object. Sample (Safari):
      //   "undefined is not an object (evaluating 'response.cashbackReminder')"
      // See WC-CALENDAR-7. Nothing user-facing breaks; not our code.
      /cashbackReminder/,
      // iOS WKWebView in-app browsers (apps embedding a Safari WebView) throw
      // this when the native host doesn't acknowledge a JS-to-native
      // postMessage. Fires from inside the in-app browser's bridge, not our
      // code, and nothing user-facing breaks. See WC-CALENDAR-A.
      /WKWebView API client did not respond to this postMessage/,
      // Browser extensions / in-app browsers (password managers, form fillers,
      // shopping and accessibility overlays) walk the DOM and JSON.stringify our
      // elements. A React-managed node carries a circular __reactFiber
      // back-reference, so V8 throws "Converting circular structure to JSON
      // --> starting at object with constructor 'HTMLAnchorElement'". Thrown from
      // the injected script, not us (we only stringify plain request payloads),
      // and nothing user-facing breaks. See WC-CALENDAR-G.
      /Converting circular structure to JSON/,
    ],
    // Drop any event whose top stack frame originates inside a browser
    // extension. Covers MetaMask, ad blockers, password managers, accessibility
    // overlays, and every other extension that injects into our pages.
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      // PostHog's scripts (session-replay recorder, array.js, etc.) are proxied
      // through our own origin at /ingest/static/* (see vercel.json). When an ad
      // blocker, corporate proxy or a transient CDN hiccup returns a non-JS body
      // (an HTML block/error page) for one of them, the browser throws
      // "SyntaxError: Invalid or unexpected token" parsing it (posthog-recorder.js
      // is a classic ad-blocker target). Third-party code, never ours, and only
      // session replay is affected, so drop it. See WC-CALENDAR-B.
      /\/ingest\/static\//i,
    ],
  });
}

// Migrate legacy hash URLs (e.g. /#/calendar?token=abc) to clean paths
if (window.location.hash.startsWith("#/")) {
  const hashPath = window.location.hash.slice(1); // "#/calendar?x=y" -> "/calendar?x=y"
  history.replaceState(null, "", hashPath);
}

// Backwards compat: redirect ?lang=xx to /xx/ path (client-side fallback
// in case middleware didn't catch it, e.g. dev mode)
const langParam = new URLSearchParams(window.location.search).get("lang");
if (langParam) {
  const params = new URLSearchParams(window.location.search);
  params.delete("lang");
  const remaining = params.toString();
  const basePath = window.location.pathname === "/" ? "" : window.location.pathname;
  const newPath = langParam === "en"
    ? `${basePath || "/"}${remaining ? "?" + remaining : ""}`
    : `/${langParam}${basePath}${remaining ? "?" + remaining : ""}`;
  window.location.replace(newPath);
}

// Capture UTM / Reddit Click ID attribution from URL params
captureAttribution();

// Render error fallback — also gives Sentry a chance to capture the error.
// Without ErrorBoundary, render-phase exceptions propagate to React's root
// handler and are swallowed silently in production builds (no Sentry capture,
// no visible UI). The picker-crash regression of May 2026 went undetected
// for hours because of this; do not remove this wrapper.
function RootErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground text-sm">
          We've been notified and are looking into it. Please refresh the page.
        </p>
        <button
          type="button"
          onClick={resetError}
          className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Try again
        </button>
        {import.meta.env.DEV && error instanceof Error ? (
          <pre className="mt-4 whitespace-pre-wrap text-left text-xs text-destructive">{error.message}</pre>
        ) : null}
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={RootErrorFallback} showDialog={false}>
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
      <App />
    </Suspense>
  </Sentry.ErrorBoundary>
);
